import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaListCard from "@/components/base/DramaListCard";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import FilterSidebar from "@/pages/category/components/FilterSidebar";
import { PAGE_SIZE, SORT_OPTIONS, filterDramas } from "@/lib/drama";
import type { DramaFilter, SortKey } from "@/types/drama";

const DEFAULT_FILTER: DramaFilter = { genre: "全部", region: "全部", year: "全部" };

export default function Category() {
  const [params, setParams] = useSearchParams();

  const filter: DramaFilter = {
    genre: params.get("genre") || DEFAULT_FILTER.genre,
    region: params.get("region") || DEFAULT_FILTER.region,
    year: params.get("year") || DEFAULT_FILTER.year,
  };
  const rawSort = params.get("sort");
  const sort: SortKey =
    rawSort === "heat" || rawSort === "rating" || rawSort === "update" ? rawSort : "update";
  const requestedPage = Number(params.get("page") || "1");

  const results = filterDramas(filter, sort);
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const page = Math.min(Math.max(1, Number.isNaN(requestedPage) ? 1 : requestedPage), totalPages);
  const visible = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const updateParam = (key: string, value: string, resetPage = true) => {
    const next = new URLSearchParams(params);
    const isDefault =
      !value || value === "全部" || (key === "sort" && value === "update") || (key === "page" && value === "1");
    if (isDefault) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (resetPage && key !== "page") next.delete("page");
    setParams(next);
  };

  const handleReset = () => setParams(new URLSearchParams());

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main className="pt-16 md:pt-[72px]">
        <div className="border-b border-background-200 bg-background-50">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-7 md:px-8 md:py-9">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <span className="text-foreground-800">电视剧分类</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              电视剧分类
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              精选职场、都市、情感、悬疑等题材剧集，支持按类型、地区、年份筛选，并按更新时间或热度排序，
              快速找到你想看的那一部。
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:gap-6">
            <aside className="w-full shrink-0 lg:w-[248px]">
              <div className="lg:sticky lg:top-[88px]">
                <FilterSidebar
                  filter={filter}
                  onChange={(key, value) => updateParam(key, value)}
                  onReset={handleReset}
                  resultCount={results.length}
                />
                <div className="mt-4 hidden rounded-lg border border-background-200 bg-background-50 p-4 lg:block">
                  <p className="flex items-center gap-1.5 text-xs font-medium text-foreground-900">
                    <i className="ri-lightbulb-flash-line text-accent-600" aria-hidden="true" />
                    筛选小贴士
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                    点击「全部」可取消当前条件；多个条件可叠加使用，结果会自动刷新。
                  </p>
                </div>
              </div>
            </aside>

            <div className="min-w-0 flex-1">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                <p className="text-xs text-foreground-600 md:text-sm">
                  共 <span className="font-semibold text-primary-700">{results.length}</span> 部剧集
                  <span className="ml-2 hidden text-foreground-400 sm:inline">
                    第 {page} / {totalPages} 页
                  </span>
                </p>
                <div className="flex items-center gap-1.5">
                  <span className="hidden text-xs text-foreground-500 sm:inline">排序</span>
                  <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => updateParam("sort", option.key)}
                        className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                          sort === option.key
                            ? "bg-primary-500 font-medium text-background-50"
                            : "text-foreground-600 hover:text-primary-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {visible.length > 0 ? (
                <div className="grid gap-3">
                  {visible.map((drama) => (
                    <DramaListCard key={drama.id} drama={drama} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="未找到相关电视剧"
                  description="当前筛选条件下没有匹配的剧集，试试减少筛选条件或重置后再看看。"
                  action={
                    <button
                      type="button"
                      onClick={handleReset}
                      className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      重置筛选条件
                    </button>
                  }
                />
              )}

              <Pagination
                page={page}
                totalPages={totalPages}
                onChange={(nextPage) => {
                  updateParam("page", String(nextPage), false);
                  window.scrollTo({ top: 220, behavior: "smooth" });
                }}
              />
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}